import type { LandingCountryConfig } from "./types";
import { STRIPE_PRICE_IDS, INITIAL_MONTHLY_LEAD_CAP } from "@/config/stripe-plans";
import { COMPARISON_DISCLAIMER } from "@/config/comparisons/disclaimer";

// Illustrative monthly export volume for the "Do the Credit Math" comparison.
// Pinned to the enforced cap so the worked example matches real usage limits.
const CAP = INITIAL_MONTHLY_LEAD_CAP.toLocaleString();

// Apollo Professional's allowance, verified against apollo.io/pricing and
// apollo.io/pricing/about-credits on 2026-09-24. An email costs 1 credit, so
// this doubles as the number of contact exports it buys. Derived rather than
// restated so the scenario and the burn-rate can never drift apart.
const APOLLO_ANNUAL_CREDITS = 48_000;
const APOLLO_MONTHS_COVERED = Math.floor(
  APOLLO_ANNUAL_CREDITS / INITIAL_MONTHLY_LEAD_CAP,
);

// The one true B2B landing config. Country files (asia/india/uae) layer on top
// via deepMerge (arrays replace wholesale). Positioning: a flat-price B2B
// contact database for teams tired of Apollo/ZoomInfo credit metering. Every
// claim must be defensible — no intent/"in-market" language (the data is a
// static verified-contact database, not an intent signal).
export const defaultCountryConfig: LandingCountryConfig = {
  slug: "default",
  metadata: {
    // Brand-less — app/layout.tsx appends " | LeadsMiner" via title template.
    title: "B2B Contact Database, No Credits: $49.99/mo",
    description:
      "A flat-rate B2B contact database. 158M+ contacts with verified emails and direct dials for $49.99/mo. Unlimited exports, no credits, no seats, no contracts.",
    keywords: [
      "b2b contact database",
      "apollo alternative without credits",
      "zoominfo alternative flat pricing",
      "unlimited b2b contacts",
      "verified b2b emails",
      "direct dials database",
      "sales navigator alternative",
      "lead generation software",
    ],
    // OG/Twitter titles don't run through the title template, so they carry the brand.
    openGraphTitle: "All the Contacts. None of the Credits. | LeadsMiner",
    openGraphDescription:
      "158M+ B2B contacts with built-in email verification, plus direct dials, for a flat $49.99/mo. Unlimited exports and no credit meter.",
    twitterTitle: "All the Contacts. None of the Credits. | LeadsMiner",
    twitterDescription:
      "158M+ B2B contacts with built-in email verification, plus direct dials, for a flat $49.99/mo. Unlimited exports and no credit meter.",
  },
  navbar: {
    ctaTextMobile: "Get Started",
    ctaTextDesktop: "Get Started",
  },
  hero: {
    badgeText: "158M+ B2B contacts in 195 countries",
    titleLine1: "All the contacts.",
    titleLine2: "None of the credits.",
    subtitle:
      "A B2B contact database with verified emails and direct dials, flat at $49.99/mo. Export 500 or 50,000; the price doesn't move.",
    ctaText: "Get 25 Free Contacts",
    ctaSubtext: "No credit card. $49.99/mo after, cancel anytime.",
    bullets: [
      "158M+ contacts with built-in verification",
      "Emails + direct dials",
      "195 countries",
      "Cancel anytime",
      "30-day guarantee",
    ],
    trustBadges: [
      { icon: "Database", text: "158M+ Contacts" },
      { icon: "MailCheck", text: "Verified Emails" },
      { icon: "LineChart", text: "Direct Dials" },
      { icon: "Globe", text: "195 Countries" },
      { icon: "ShieldCheck", text: "GDPR Ready" },
    ],
  },
  problem: {
    heading: "The Credit Meter Is the Product You're Actually Paying For",
    body:
      'Every big prospecting tool sells you the same thing: a database with a meter on it. Export a contact — credit gone. Need the same decision maker for a second campaign? Pay again. Go over your monthly allowance? Buy a top-up. Even the plans labeled "unlimited" have credit caps buried in the fine print.\n\nYou don\'t have a data problem. You have a meter problem.',
  },
  features: {
    title: "We Removed the Meter",
    subtitle:
      "One flat $49.99/mo covers every search, reveal and export. Nothing in the product counts down.",
    stats: [
      { value: "158M+", label: "B2B Contacts", sub: "Emails verified at export + direct dials" },
      { value: "$0", label: "Per-Contact Cost", sub: "One flat $49.99/mo covers every export" },
      { value: "0", label: "Contracts & Seat Fees", sub: "Month-to-month, cancel anytime" },
    ],
    items: [
      {
        icon: "Zap",
        title: "Never Count Credits Again",
        description:
          "Export as many contacts as you need. One flat price covers all of it, and no counter runs anywhere in the product, because there's nothing to count.",
        statIcon: "Zap",
        statText: "Unlimited exports, zero credit math",
      },
      {
        icon: "ShieldCheck",
        title: "Never Pay Twice for the Same Decision Maker",
        description:
          "Reveal a contact once and it's yours. Re-export them for any campaign or client you like, at no extra cost, forever.",
        statIcon: "Zap",
        statText: "Re-export any contact, any time, free",
      },
      {
        icon: "MailCheck",
        title: "Verified Emails and Direct Dials",
        description:
          "Every email passes multi-step verification before you see it. Every phone number is a direct line to the decision maker, not a company switchboard.",
        statIcon: "Zap",
        statText: "Real contact info, not guesswork",
      },
    ],
  },
  dataSection: {
    heading: "A B2B contact database you can filter 50+ ways",
    body:
      "Filter by industry, job title, seniority, department, company size, technology and geography across 195 countries. Build a list of SaaS founders in Austin, or procurement directors in Germany, in under a minute. Then export the whole thing without watching a credit balance drain.",
  },
  creditMath: {
    title: "Do the Credit Math",
    subtitle:
      "Same 158M-contact scale. Very different bill. Here's how far published pricing actually gets you at real volume.",
    scenarioLabel: `The math on ${CAP} contacts a month`,
    columns: [
      {
        name: "Apollo (Professional)",
        price: "$79/user/mo, billed annually (published)",
        allowance: `${APOLLO_ANNUAL_CREDITS.toLocaleString()} credits/user/year, granted upfront`,
        caveats: [
          "An email costs 1 credit; a phone number costs 8",
          "Credits expire at the end of each billing cycle and never roll over",
          "The year's credits arrive upfront, so a heavy month borrows from a later one",
        ],
        // No dollar total: Apollo publishes no rate for credits past the
        // allowance (checked /pricing, /pricing/about-credits and
        // /pricing/calculator on 2026-09-24), so one cannot be computed from
        // published pricing without inventing the missing operand.
        monthlyTotal: `$79/user/mo covers about ${APOLLO_MONTHS_COVERED} months at ${CAP} exports a month. Apollo doesn't publish what the rest costs.`,
        provenance: {
          source: "https://www.apollo.io/pricing",
          checkedOn: "2026-09-24",
        },
      },
      {
        name: "ZoomInfo",
        price: "Not published",
        allowance: "Quoted per account, on an annual contract",
        // Was a set of unsourced third-party ranges of unknown vintage. These
        // are ZoomInfo's own numbers from its FY2025 results (8-K ex-99.1,
        // filed 2026-02-09), which is the closest thing to a public price a
        // company without a rate card produces.
        caveats: [
          "No rate card: every price starts as a sales conversation",
          "1,921 customers contract at $100,000+ a year",
          "Those customers are more than half of all company ACV",
        ],
        monthlyTotal: "Whatever you negotiate. You find out after the demo.",
        // ZoomInfo publishes no rate card, so the source is its own filed
        // results: 8-K exhibit 99.1, Q4 and full-year 2025, filed 2026-02-09.
        provenance: {
          source:
            "https://www.sec.gov/Archives/edgar/data/1794515/000179451526000004/zi-8kex991x20260209.htm",
          checkedOn: "2026-09-24",
        },
      },
      {
        name: "LeadsMiner",
        price: "$49.99/mo flat",
        allowance: "Unlimited exports, every contact included",
        caveats: [
          "No credits or overage charges",
          "Re-export any contact free, always",
          "No seats. Your whole team is included",
          "No contract. Cancel anytime",
        ],
        monthlyTotal: "$49.99/mo. That's it.",
        highlight: true,
      },
    ],
    footnote: COMPARISON_DISCLAIMER,
    ctaText: "Stop Renting Credits. Start for $49.99/mo",
  },
  howItWorks: {
    title: "From Search to Sender-Ready in Three Steps",
    subtitle:
      "Build your list, verify the emails, then export a sender-ready CSV for Instantly, Smartlead or Lemlist. The price is the same whatever the volume.",
    steps: [
      {
        icon: "Filter",
        label: "STEP 01",
        title: "Build Your Search",
        description:
          "Stack filters on title, industry, size and location until the list is exactly your ICP.",
      },
      {
        icon: "Cpu",
        label: "STEP 02",
        title: "Verify Before You Send",
        description:
          "Built-in verification checks every email at export, so you send a clean list instead of guessing. Pattern-guessed addresses are badged, never passed off as verified.",
      },
      {
        icon: "Download",
        label: "STEP 03",
        title: "Export Sender-Ready",
        description:
          "One-click CSVs formatted for Instantly, Smartlead and Lemlist, with guessed emails excluded by default. Export 500 or 50,000 for the same flat price.",
      },
    ],
    ctaText: "Start Free, No Card",
    footnote:
      "Start with 25 free contacts • No credit card • Upgrade anytime for $49.99/mo",
  },
  audience: {
    title: "Built for People Who Live on Lead Flow",
    segments: [
      {
        label: "Freelancers & solo operators",
        body:
          "Your whole business is leads. On credit tools, every client campaign eats your margin. On LeadsMiner, your data cost is $49.99 whether you run one campaign or twenty.",
      },
      {
        label: "Agencies",
        body:
          "Onboard a new client without recalculating your credit budget. Unlimited contact exports means client #10 costs you the same as client #1: nothing extra.",
      },
      {
        label: "Founders & sales teams",
        body:
          "Build pipeline without asking finance for a credit top-up. One flat line item, predictable forever.",
      },
    ],
  },
  stats: {
    title: "What B2B teams are saying",
    // Empty until we have real customer quotes — the section renders nothing.
    testimonials: [],
  },
  pricing: {
    title: "One Plan. No Asterisks.",
    subtitle: "$49.99/month · month-to-month · cancel anytime",
    buyNowText: "Start Mining for $49.99/mo",
    reassurance:
      "If you don't replace your credit tool in 30 days, we'll refund every cent.",
    plans: [
      {
        name: "Unlimited",
        description: "Everything a B2B sales team needs to fill the pipeline",
        badge: "Best Value",
        price: "$49.99",
        period: "/month",
        planCode: "unlimited",
        priceId: STRIPE_PRICE_IDS.subscription.unlimited,
        features: [
          "Unlimited searches, reveals, and contact exports",
          "158M+ B2B contacts with built-in email verification, emails plus direct dials",
          "50+ filters across 195 countries",
          "CSV, Excel and Google Sheets export, sender-ready for Instantly, Smartlead and Lemlist",
          "No credits, no seats, no contracts",
          "Priority support",
          "30-day money-back guarantee",
        ],
        borderColor: "border-primary shadow-xl shadow-primary/20 ring-1 ring-primary",
        buttonStyle: "bg-primary-surface text-primary-foreground hover:bg-primary-surface/90 transition-opacity",
        popular: true,
      },
    ],
    pledge: {
      title: "The No-Tricks Pledge",
      items: [
        "Flat $49.99/mo: the price at signup is the price at every renewal",
        "No seats, no per-user pricing",
        "No credit expiry — re-export any contact forever",
        "No overage fees, ever",
        "Cancel anytime in two clicks, with no notice period",
        `Our one limit, in plain sight: a fair-use ceiling of ${CAP} contacts/mo against abuse, raised on request`,
      ],
    },
  },
  faq: {
    title: "Frequently Asked Questions",
    subtitle: "Everything B2B sales teams ask before switching.",
    items: [
      {
        question: "Do you charge credits?",
        answer:
          "No. There is no credit system anywhere in LeadsMiner. Search, reveal and export as much as you want; it's all included in $49.99/mo.",
      },
      {
        question: "Is it really unlimited?",
        answer: `Yes. No credit meter, no per-contact charge, and nothing buried in fine print. Our one limit is stated right on the pricing card: a fair-use ceiling of ${CAP} contacts per month to stop abuse, raised on request (usually within 24 hours). Everything under that ceiling is included in the flat $49.99/mo.`,
      },
      {
        question: "How is this different from Apollo or ZoomInfo?",
        answer:
          "The data is comparable in scale: 158M+ B2B contacts with built-in email verification. The difference is the business model. They meter every export with credits and contracts; we charge one flat monthly price for everything. Competitor details are based on published information and may change, so see our comparison pages.",
      },
      {
        question: "How much does a B2B contact database cost?",
        answer:
          "It depends on how the vendor meters you. Most charge per credit or per seat on an annual contract, so the real number moves with how much you export and is hard to forecast in advance. LeadsMiner is $49.99/mo flat whatever the volume. The credit math section above works the comparison out in full on published pricing.",
      },
      {
        question: "Are the phone numbers direct dials or company switchboards?",
        answer:
          "Direct dials. The phone numbers in the database are direct lines to the decision maker rather than company switchboards, and re-revealing one costs nothing because nothing in the product counts down.",
      },
      {
        question: "Will I ever pay again for a contact I already revealed?",
        answer:
          "Never. Once you reveal a decision maker, you can re-export them for any campaign or client at no additional cost.",
      },
      {
        question: "Where do you source the contact data?",
        answer:
          "Public business databases, LinkedIn profiles, and our partner network. Every email passes a verification pipeline before it reaches you, and we update the dataset continuously.",
      },
      {
        question: "Is the data GDPR / CCPA compliant?",
        answer:
          "Yes. We follow GDPR, CCPA, and CAN-SPAM. Every contact has a documented lawful basis and the right to request deletion.",
      },
      {
        question: "Can I cancel anytime?",
        answer:
          "Yes. Month-to-month billing, cancel in one click from your dashboard, and a 30-day money-back guarantee on your first month.",
      },
    ],
  },
  cta: {
    badgeText: "30-Day Money-Back Guarantee",
    title: "Ready to Stop Doing\nCredit Math?",
    subtitle:
      "Get every verified contact you need for one flat $49.99/mo, with no credits and no contract. Try it for 30 days; if it's not for you, get your money back.",
    buttonText: "Start Mining for $49.99/mo",
  },
  footer: {
    brandName: "LeadsMiner",
    tagline: "All the contacts. None of the credits.",
  },
};
